import { Key } from 'react'

import { useCreation, useMemoizedFn } from 'ahooks'
import { Cascader } from 'antd'
import { isEmpty, isNil, last } from 'lodash'

import { KindDetail } from '@eastcoal/api/system/interface'

import { DataError } from '@eastcoal/common/components/DataError'
import { findTreePaths } from '@eastcoal/common/utils/array'
import { omit, pick } from '@eastcoal/common/utils/object'

import { useKindAllNodeStore } from '../../Store/hooks/useStore'

import { KindAllNodeCascaderCoreProps } from './interface'
// 多选时,将父节点选择独立出去,同时保留全选交互。
const copyParentDataAsChildNode = (data?: KindDetail[]) => {
  if (!data?.length) {
    return []
  }
  const newKindTree: KindDetail[] = []
  data.forEach(kind => {
    const newKind = { ...kind }
    if (kind.children?.length) {
      newKind.children = [
        { ...omit(kind, ['children']) },
        ...copyParentDataAsChildNode(kind.children)
      ]

      newKind['id'] = `${newKind.id}_p`
    }
    newKindTree.push(newKind)
  })
  return newKindTree
}
const fieldNames = {
  value: 'id',
  label: 'name',
  children: 'children'
} as const

export const KindAllNodeCascaderCore = ({
  allowClear,
  disabled,
  placeholder
}: KindAllNodeCascaderCoreProps) => {
  const {
    data,
    error,
    loading,
    reload,
    multiple,
    selectedKeys,
    setSelectedKeys
  } = useKindAllNodeStore(state =>
    pick(state, [
      'error',
      'loading',
      'reload',
      'multiple',
      'selectedKeys',
      'setSelectedKeys',
      'data'
    ])
  )
  const finalData = useCreation(
    () => (multiple ? copyParentDataAsChildNode(data) : data),
    [multiple, data]
  )
  const handleChange = useMemoizedFn((keys?: Key[] | Key[][]) => {
    // 清空
    if (isNil(keys) || keys.length === 0) {
      setSelectedKeys([])
    }
    // 多选
    else if (multiple) {
      setSelectedKeys((keys as Key[][]).map(item => `${last(item)}`))
    }
    // 单选
    else {
      setSelectedKeys([`${last(keys as string[])}`])
    }
  })

  const value = useCreation(() => {
    if (isEmpty(finalData)) {
      return undefined
    }

    if (isEmpty(selectedKeys)) {
      return undefined
    }

    const nodesArr = selectedKeys.map(key =>
      findTreePaths(finalData, node => node.id === key)
    )
    const paths = nodesArr.map(nodes => nodes.map(node => `${node.id}`))

    const keys = multiple ? paths : paths[0]
    return keys as any
  }, [selectedKeys, multiple, finalData])

  return error ? (
    <DataError error={error} loading={loading} reload={reload} />
  ) : (
    <Cascader
      changeOnSelect
      showSearch
      allowClear={allowClear}
      disabled={disabled}
      expandTrigger='hover'
      fieldNames={fieldNames}
      loading={loading}
      multiple={multiple}
      options={finalData}
      placeholder={placeholder}
      showCheckedStrategy={Cascader.SHOW_CHILD}
      value={value}
      onChange={handleChange}
    />
  )
}
